import { Button, Card, Col, DatePicker, Input, Row, Table, Select } from "antd";
import React, { useState, useEffect } from "react";
import useDebounce from "../hooks/useDebounce";
import { EyeOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import moment from "moment";
import { useNavigate } from "react-router";

const { RangePicker } = DatePicker;
const { Option } = Select;

const CustomTable = ({
  filters = {},
  setFilters = () => {},
  fields = { range: true, search: true, day: false }, // 👈 added day
  addFn = false,
  customFilter = null,
  data = [],
  columns = [],
  totalDocs = 10,
  loading = false,
  rowKey = "_id",
  searchField = ["name"],
  heading = false,
  searchPlaceholder,
  tableProps = {},
  pagination = true,
  resetBtn = false,
  customView = false,
  components = {},
  resetProps = {},
  showCompareBranch = false,
}) => {
  const [tableLoading, setTableLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [dayFilter, setDayFilter] = useState(filters.day || "All"); // 👈 for day filter
  const navigate = useNavigate();

  useEffect(() => {
    setTableLoading(loading);
  }, [loading, data]);

  const debouncedSearch = useDebounce((searchTerm) => {
    setTableLoading(true);
    const searchTermObj = searchField.reduce((acc, curr) => {
      acc[curr] = searchTerm;
      return acc;
    }, {});
    setFilters((prev) => ({
      ...prev,
      ...searchTermObj,
      page: 1,
    }));
  }, 500);

  const handleSearch = (e) => {
    setSearchInput(e.target.value);
    debouncedSearch(e.target.value);
  };

  const handleTableChange = (pagination, filters) => {
    setTableLoading(true);
    setFilters((prev) => ({
      ...prev,
      page: pagination.current,
      limit: pagination.pageSize,
      ...filters,
    }));
  };

  const handleDateRangeChange = (dates, dateStrings) => {
    setTableLoading(true);
    const [startDate, endDate] = dateStrings;
    setFilters((prev) => ({
      ...prev,
      startDate,
      endDate,
      page: 1,
    }));
  };

  const handleDayChange = (value) => {
    setTableLoading(true);
    setDayFilter(value);
    setFilters((prev) => ({
      ...prev,
      day: value,
      page: 1,
    }));
  };

  const handleReset = () => {
    setTableLoading(true);
    setSearchInput("");
    setDayFilter("All");
    setFilters((prev) => ({
      ...resetProps,
    }));
  };

  return (
    <Row gutter={[24, 0]}>
      <Col xs={24} xl={24}>
        <Card bordered={false} className="criclebox tablespace mb-24">
          <div className="tab_inner_tit">
            {heading && <div className="title-left">{heading}</div>}
            <div className="tab-upload-wrap d-flex align-items-end justify-content-end flex-wrap">
              <div className="d-flex align-items-end gap-3 flex-wrap">
                {/* 🔍 Search */}
                {fields.search && (
                  <div className="pageHeadingSearch">
                    <Input.Search
                      value={searchInput}
                      placeholder={searchPlaceholder || "Search..."}
                      className="searchInput"
                      allowClear
                      onChange={handleSearch}
                    />
                  </div>
                )}

                {/* 📅 Range */}
                {fields.range && (
                  <div className="role-wrap">
                    <RangePicker
                      value={[
                        filters?.startDate
                          ? moment(filters.startDate, "DD/MM/YYYY")
                          : null,
                        filters?.endDate
                          ? moment(filters.endDate, "DD/MM/YYYY")
                          : null,
                      ]}
                      style={{ width: 200 }}
                      format="DD/MM/YYYY"
                      onChange={handleDateRangeChange}
                    />
                  </div>
                )}

                {/* 📌 Day Filter */}
                {fields.day && (
                  <div className="role-wrap">
                    <Select
                      value={dayFilter}
                      onChange={handleDayChange}
                      style={{ width: 200 }}
                    >
                      <Option value="All">All Days</Option>
                      <Option value="Monday">Monday</Option>
                      <Option value="Tuesday">Tuesday</Option>
                      <Option value="Wednesday">Wednesday</Option>
                      <Option value="Thursday">Thursday</Option>
                      <Option value="Friday">Friday</Option>
                      <Option value="Saturday">Saturday</Option>
                      <Option value="Sunday">Sunday</Option>
                    </Select>
                  </div>
                )}

                {customFilter && customFilter()}

                {resetBtn && (
                  <Button
                    className="main-new-reset-button"
                    onClick={handleReset}
                    icon={<ReloadOutlined />}
                  >
                    Reset
                  </Button>
                )}

                {customView && customView}

                {addFn && (
                  <Button
                    type="primary"
                    onClick={addFn}
                    icon={<PlusOutlined />}
                  >
                    Add
                  </Button>
                )}

                {showCompareBranch && (
                  <Button
                    type="primary"
                    onClick={() => navigate("/branches/compare-branches")}
                  >
                    Compare Branch
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="table-responsive customPagination">
            <Table
              {...tableProps}
              dataSource={data}
              columns={columns}
              pagination={
                pagination && {
                  pageSize: filters.limit || 10,
                  current: filters.page || 1,
                  total: totalDocs || 0,
                  pageSizeOptions: [10, 20, 50, 100, 500],
                  showSizeChanger: true,
                  hideOnSinglePage: false,
                  ...(typeof pagination === "object" ? pagination : {}),
                }
              }
              onChange={handleTableChange}
              rowKey={rowKey}
              loading={tableLoading}
              components={components}
            />
          </div>
        </Card>
      </Col>
    </Row>
  );
};

export default CustomTable;
